import {
	AlertTriangle,
	BookOpen,
	CheckCircle,
	Info,
	Unplug,
	XCircle,
} from "lucide-react";
import type * as React from "react";
import { cn } from "@/util/string";

export type AlertBarVariant =
	| "error"
	| "warning"
	| "info"
	| "success"
	| "default"
	| "docs";

interface AlertBarProps
	extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
	variant?: AlertBarVariant;
	title?: React.ReactNode;
	children?: React.ReactNode;
	action?: React.ReactNode;
	icon?: React.ElementType;
}

const variantStyles: Record<AlertBarVariant, string> = {
	error: "bg-error text-error-content",
	warning: "bg-warning text-warning-content",
	info: "bg-info text-info-content",
	success: "bg-success text-success-content",
	default:
		"border border-purple-500/20 bg-linear-to-b from-purple-500/5 via-base-200/50 to-base-200/30",
	docs: "border border-info/20 bg-info/5 text-base-content/80",
};

const variantIcons: Record<AlertBarVariant, React.ElementType> = {
	error: XCircle,
	warning: AlertTriangle,
	info: Info,
	success: CheckCircle,
	default: Unplug,
	docs: BookOpen,
};

export function AlertBar({
	variant = "info",
	title,
	children,
	action,
	icon,
	className,
	...props
}: AlertBarProps) {
	const Icon = icon ?? variantIcons[variant];

	return (
		<div
			role="alert"
			className={cn(
				"alert mb-6 shadow-lg flex-wrap!",
				variantStyles[variant],
				className,
			)}
			{...props}
		>
			<Icon className="size-6 shrink-0" />
			<div className="flex flex-col">
				{title && <span className="font-bold">{title}</span>}
				{children && <span className="text-sm">{children}</span>}
			</div>
			{action && <div className="flex-none">{action}</div>}
		</div>
	);
}
